import { RoutePaths } from '../../Routes';
import { LocalStorageService } from '../../services/localStorageService';
import { history } from '../../helpers/history';
import { userActions } from '../user/actions';
import { ApiService } from '../../services/apiService';
import { notificationActions } from '../notification/actions';
import { Message } from '../notification/types';
import {
  AuthActionTypes,
  LOGGED_IN,
  LOGGED_OUT,
  SET_REFRESH_IN_PROGRESS,
} from './types';
import { globalActions } from '../actions';
import { ApplicationState, AppDispatch } from '..';
import jwtDecode from 'jwt-decode';
import { clearEntraSession, isEntraSignedIn } from '../../auth/msalConfig';

const expirationOffsetInMinutes = 10;

function loggedIn(token: string, refreshToken: string): AuthActionTypes {
  return {
    type: LOGGED_IN,
    token,
    refreshToken,
  };
}

function setRefreshInProgress(refreshInProgress: boolean): AuthActionTypes {
  return {
    type: SET_REFRESH_IN_PROGRESS,
    refreshInProgress,
  };
}

function login(username: string, password: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await ApiService.login(username, password);

      dispatch(globalActions.clearUserCache); // clear any dangling user cache after successfull login, just to be safe.

      const token = response.data.token;
      const refreshToken = response.data.refreshToken;
      LocalStorageService.setUsername(username);
      dispatch(userActions.setUsername(username));
      history.push(RoutePaths.Overview);
      return dispatch(loggedIn(token, refreshToken));
    } catch {
      return dispatch(
        notificationActions.addMessage(
          Message.error('errors.login_failed').withTranslatedTextAndOptions({
            username,
          }),
        ),
      );
    }
  };
}

function loginWithEntra(idToken: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await ApiService.entraLogin(idToken);

      dispatch(globalActions.clearUserCache()); // clear any dangling user cache after successful login, just to be safe.

      const token = response.data.token;
      const refreshToken = response.data.refreshToken;
      const username = jwtDecode<{ sub: string }>(token).sub;
      LocalStorageService.setUsername(username);
      dispatch(userActions.setUsername(username));
      history.push(RoutePaths.Overview);
      return dispatch(loggedIn(token, refreshToken));
    } catch (err) {
      const tenantRejected =
        (err as { response?: { data?: { errorCode?: string } } })?.response
          ?.data?.errorCode === 'entra_tenant_not_allowed';
      return dispatch(
        notificationActions.addMessage(
          Message.error(
            tenantRejected
              ? 'errors.entra_tenant_not_allowed'
              : 'errors.entra_login_failed',
          ).withTranslatedText(),
        ),
      );
    }
  };
}

function logout() {
  return (dispatch: AppDispatch) => {
    LocalStorageService.clearToken();
    LocalStorageService.clearUsername();
    dispatch({ type: LOGGED_OUT });
    dispatch(globalActions.clearUserCache());
    if (isEntraSignedIn()) {
      // Clears the MSAL session and returns to the login page
      clearEntraSession();
      return;
    }
    history.push(RoutePaths.Login);
  };
}

function refreshTokenIfExpired() {
  return async (dispatch: AppDispatch, getState: () => ApplicationState) => {
    const { username, refreshToken, tokenExpiresAt, refreshInProgress } =
      getState().auth;
    const refDate = new Date();
    refDate.setMinutes(refDate.getMinutes() + expirationOffsetInMinutes); // refresh x minutes before expiration
    if (
      !refreshInProgress && // avoid refresh token race conditions
      username &&
      refreshToken &&
      tokenExpiresAt &&
      tokenExpiresAt < refDate
    ) {
      try {
        dispatch(setRefreshInProgress(true));
        const response = await ApiService.refreshToken(username, refreshToken);
        const newToken = response.data.token;
        const newRefreshToken = response.data.refreshToken;
        dispatch(setRefreshInProgress(false));
        return dispatch(loggedIn(newToken, newRefreshToken));
      } catch (error) {
        // TODO: handle refresh token error
        // eslint-disable-next-line no-console
        console.warn('Refresh token failed', error);
        dispatch(setRefreshInProgress(false));
      }
    }
  };
}

export const authActions = {
  login,
  loginWithEntra,
  logout,
  refreshTokenIfExpired,
};
